/**
 * TourSistema — tour interactivo con react-joyride v3.
 *
 * IMPORTANTE (v3 API):
 *  - El prop es `onEvent`, no `callback`
 *  - Botones se configuran con `buttons: ['back', 'primary', 'skip', 'close']`
 *  - `overlayClickAction` reemplaza a `disableOverlayClose`
 *  - `blockTargetInteraction` reemplaza a `spotlightClicks` (inverted)
 *  - Modo UNCONTROLLED: no manejamos stepIndex nosotros; Joyride avanza solo
 *  - El callback recibe (data, controls) — usamos controls.stop() para cerrar
 *
 * Fallbacks defensivos post-bug de producción:
 *  - localStorage.tour_dismissed evita re-showing
 *  - Botón X flotante siempre disponible como escape
 *  - Sólo auto-arranca si Layout ya renderizó (data-testid ayuda-btn existe)
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Joyride, STATUS, EVENTS } from 'react-joyride';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';

const stepsTaller = [
  { target: 'body', placement: 'center', disableBeacon: true,
    title: '¡Bienvenido a TalleresPRO!',
    content: 'Este tour de 2 minutos te muestra los módulos clave. Puedes saltarlo con el botón "X" o "Saltar tour" en cualquier momento.' },
  { target: '[href="/dashboard"]', title: 'Inicio · Dashboard', content: 'Aquí ves el resumen del día: órdenes activas, ingresos y clientes pendientes.' },
  { target: '[href="/clientes"]', title: 'Clientes', content: 'Registra a tus clientes con teléfono, email y sus vehículos. El sistema recuerda su historial y suma visitas para descuentos por lealtad.' },
  { target: '[href="/vehiculos"]', title: 'Vehículos', content: 'Cada vehículo lleva su historial de mantenimiento: qué se le hizo, cuándo y cuánto se cobró.' },
  { target: '[href="/ordenes"]', title: 'Órdenes de servicio', content: 'El corazón del taller. Registras cliente, vehículo, servicios, refacciones y total.' },
  { target: '[href="/pagos"]', title: 'Pagos', content: 'Consulta todos los pagos recibidos y genera recibos imprimibles.' },
  { target: '[href="/corte-caja"]', title: 'Corte de caja', content: 'Al final del día haces tu corte: cuánto entró por cada método y qué queda en efectivo.' },
  { target: '[href="/inventario"]', title: 'Inventario', content: 'Controla tu stock. Se resta automáticamente al usarlo en una orden.' },
  { target: '[href="/staff"]', title: 'Staff', content: 'Da de alta mecánicos y administrativos. Cada uno entra con su usuario.' },
  { target: '[href="/citas"]', title: 'Agenda online', content: 'Recibe citas por un link público que compartes con tus clientes.' },
  { target: '[href="/suscripcion"]', title: 'Suscripción', content: 'Consulta tu plan y las herramientas incluidas — fotos de recepción, evidencias y más.' },
  { target: '[data-testid="ayuda-btn"]', title: '¿Necesitas ayuda?', placement: 'left',
    content: 'Este botón siempre está aquí. Capacitación, dudas o repetir este tour cuando quieras.' },
  { target: 'body', placement: 'center', title: '¡Listo! 🚀',
    content: 'Crea tu primer cliente, agrega su vehículo y genera una orden de prueba desde el menú lateral. ¡A operar!' },
];

const stepsCarwash = [
  { target: 'body', placement: 'center', disableBeacon: true,
    title: '¡Bienvenido a WashPro!',
    content: 'Este tour de 2 minutos te muestra cómo operar tu car wash. Puedes saltarlo con el botón "X" o "Saltar tour" en cualquier momento.' },
  { target: '[href="/carwash"]', title: 'Inicio · Dashboard', content: 'Resumen del día: turnos activos, ingresos y servicios más vendidos.' },
  { target: '[href="/carwash/turnos"]', title: 'Turnos', content: 'Cada auto se registra como turno con placa, tipo y servicios. Pasa de "recibido" a "lavado" a "entregado" hasta cobrar.' },
  { target: '[href="/carwash/mostrador"]', title: 'Mostrador (POS)', content: 'Vende productos extras (shampoo, ambientadores) sin necesidad de abrir turno.' },
  { target: '[href="/carwash/servicios"]', title: 'Servicios', content: 'Define paquetes con precio por tipo de vehículo y comisión por operario.' },
  { target: '[href="/carwash/caja"]', title: 'Caja del día', content: 'Ve en vivo lo ingresado hoy, desglosado por método de pago y operario.' },
  { target: '[href="/carwash/clientes"]', title: 'Clientes', content: 'Registra clientes frecuentes para descuentos por lealtad automáticos.' },
  { target: '[href="/carwash/nomina"]', title: 'Nómina', content: 'Calcula automáticamente lo que le toca a cada operario según sus turnos atendidos.' },
  { target: '[href="/carwash/agenda"]', title: 'Agenda online', content: 'Genera un link público para que tus clientes reserven solos por WhatsApp o redes.' },
  { target: '[href="/suscripcion"]', title: 'Suscripción', content: 'Consulta tu plan y las herramientas incluidas — ticket térmico, fotos de recepción y más.' },
  { target: '[data-testid="ayuda-btn"]', title: '¿Necesitas ayuda?', placement: 'left',
    content: 'Este botón siempre está aquí. Capacitación, dudas o repetir este tour cuando quieras.' },
  { target: 'body', placement: 'center', title: '¡Listo! 💧',
    content: 'Crea tus servicios primero, da de alta 1-2 operarios y arranca abriendo tu primer turno. ¡A trabajar!' },
];

export default function TourSistema() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const controlsRef = useRef(null);

  const esCarwash = usuario?.tipo_negocio === 'carwash';
  const steps = esCarwash ? stepsCarwash : stepsTaller;
  const rutaBase = esCarwash ? '/carwash' : '/dashboard';

  // Auto-iniciar tour — solo si el usuario está en ruta base y Layout está listo
  useEffect(() => {
    if (!usuario || usuario.rol === 'super_admin') return;
    if (localStorage.getItem('tour_dismissed') === '1') return;
    if (location.pathname !== rutaBase) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/auth/me');
        if (cancelled) return;
        if (r.data?.tour_completado) return;
        setTimeout(() => {
          if (cancelled) return;
          if (!document.querySelector('[data-testid="ayuda-btn"]')) return;
          setRun(true);
        }, 2000);
      } catch { /* silencio */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [usuario, location.pathname, rutaBase]);

  // Evento manual desde el botón de ayuda
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('tour_dismissed');
      if (location.pathname !== rutaBase) navigate(rutaBase);
      setTimeout(() => {
        if (!document.querySelector('[data-testid="ayuda-btn"]')) return;
        setRun(true);
      }, 600);
    };
    window.addEventListener('iniciar-tour', handler);
    return () => window.removeEventListener('iniciar-tour', handler);
    // eslint-disable-next-line
  }, [rutaBase, location.pathname]);

  const marcarCompletado = async () => {
    localStorage.setItem('tour_dismissed', '1');
    try { await api.post('/auth/tour-completado'); } catch { /* silencio */ }
  };

  const cerrarEmergencia = () => {
    setRun(false);
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch { /* silencio */ }
    }
    marcarCompletado();
  };

  // Fallback manual — Esc cierra el tour (workaround para bug de react-joyride v3
  // donde dismissKeyAction:'close' no se aplica correctamente)
  useEffect(() => {
    if (!run) return;
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrarEmergencia();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line
  }, [run]);

  // API v3: onEvent recibe (data, controls)
  const onEvent = (data, controls) => {
    controlsRef.current = controls;
    const { type, status } = data;

    // Termina el tour por skip, finish o si status es finished/skipped/paused
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      marcarCompletado();
      return;
    }
    // Si un target no se encuentra, saltar al siguiente
    if (type === EVENTS.TARGET_NOT_FOUND) {
      try { controls.next(); } catch { setRun(false); marcarCompletado(); }
      return;
    }
  };

  if (!usuario || usuario.rol === 'super_admin') return null;

  return (
    <>
      {run && (
        <button onClick={cerrarEmergencia}
          className="fixed top-4 right-4 z-[10001] bg-red-500 hover:bg-red-600 text-white rounded-full w-11 h-11 flex items-center justify-center shadow-xl font-bold text-lg"
          data-testid="tour-cerrar-emergencia"
          title="Cerrar tour">
          ✕
        </button>
      )}
      <Joyride
        steps={steps}
        run={run}
        continuous
        onEvent={onEvent}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: '¡Empezar!',
          next: 'Siguiente',
          nextWithProgress: 'Siguiente ({current} de {total})',
          open: 'Abrir',
          skip: 'Saltar tour',
        }}
        options={{
          buttons: ['back', 'skip', 'primary'],
          overlayClickAction: 'close',
          dismissKeyAction: 'close',
          blockTargetInteraction: false,
          showProgress: true,
          zIndex: 10000,
          primaryColor: '#f59e0b',
          textColor: '#e2e8f0',
          backgroundColor: '#0f172a',
          arrowColor: '#0f172a',
          overlayColor: 'rgba(0, 0, 0, 0.65)',
        }}
        styles={{
          tooltip: { borderRadius: 8, border: '1px solid #334155' },
          tooltipTitle: { color: '#f59e0b', fontWeight: 700, fontSize: 16 },
          tooltipContent: { fontSize: 13, lineHeight: 1.5, padding: '8px 0' },
          buttonPrimary: { backgroundColor: '#f59e0b', color: '#000', fontWeight: 700, borderRadius: 4, padding: '8px 16px' },
          buttonBack: { color: '#94a3b8', marginRight: 8 },
          buttonSkip: { color: '#64748b', fontSize: 12 },
          buttonClose: { color: '#ef4444', fontSize: 20, fontWeight: 700 },
        }}
      />
    </>
  );
}
